<template>
  <v-slide-group
    v-bind="$attrs"
    class="options"
    active-class="option--active"
    :value="props.value"
    :value-comparator="valueCompare"
    center-active
    :show-arrows="true"
    @change="change"
  >
    <template #prev>
      <v-btn class="arrow arrow--prev" icon><v-icon>$prev</v-icon></v-btn>
    </template>
    <v-slide-item
      v-for="item in props.items"
      :key="item.id"
      v-slot="{ active, toggle }"
      :value="item"
      class="mx-1"
    >
      <vs-button
        type="secondary"
        small
        shrink
        :class="['option', active ? 'option--active' : '']"
        :label="item.label"
        :data-test="`chip-filter-item-id-${item.id}`"
        @click="toggle"
      >
        {{ item.label }}
      </vs-button>
    </v-slide-item>
    <template #next>
      <v-btn class="arrow arrow--next" icon><v-icon>$next</v-icon></v-btn>
    </template>
  </v-slide-group>
</template>

<script setup lang="ts">
import VsButton from '@/components/vision/VsButton.vue'

type ItemType = { id: string | number | null; label: string }

const props = defineProps<{
  value?: ItemType | null
  items?: ItemType[]
}>()

const emit = defineEmits<{
  (event: 'update', value: ItemType): void
}>()

const valueCompare = (obj1?: ItemType | null, obj2?: ItemType | null) => {
  return obj1?.id === obj2?.id
}

const change = (obj: ItemType) => {
  emit('update', obj)
}
</script>

<style scoped>
.vs-button.option {
  height: 2rem;
  font-weight: 500;
  font-family: var(--font-regular);
  position: relative;
  color: var(--color-black);
  border: thin solid var(--color-grey--lighter);
  background-color: transparent;
}

.arrow--prev {
  margin-left: 6px;
}

.arrow--next {
  margin-right: 6px;
}

.vs-button.option--active {
  color: var(--color-blue);
}

.option.option--active:before {
  opacity: 0.08;
}

.option:before {
  content: '';
  background-color: currentColor;
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  border-radius: inherit;
}

:deep(.v-slide-group__prev),
:deep(.v-slide-group__next) {
  min-width: initial;
}

:deep(.v-slide-group__prev) {
  margin-left: -6px;
}

:deep(.v-slide-group__next) {
  margin-right: -6px;
}

:deep(.v-slide-group__prev--disabled),
:deep(.v-slide-group__next--disabled) {
  display: none;
}
</style>
